import { clsx } from "@/utils";
import { Icon, Typography } from "..";

export interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  /**
   * Lowest reachable value. Defaults to 0 so a caller can treat "stepped below one" as removal —
   * the Add Extras modal drops the extra entirely at that point.
   */
  min?: number;
  max?: number;
  /**
   * What is being counted ("Baby Capsule"), used for the buttons' accessible names — "Add another
   * Baby Capsule" reads far better than a pair of unlabelled + and − buttons.
   */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * One bordered unit for stepping a count: − , the value, +. The value is display-only; typing isn't
 * offered, because every use so far is a small count picked by tapping.
 *
 * Clamping lives here so callers can't drive it out of range, but what the bounds MEAN is theirs:
 * reaching `min` is just a value, not a removal.
 */
export const QuantityStepper = ({ value, onChange, min = 0, max, label, disabled, className }: QuantityStepperProps) => {
  const step = (next: number) => onChange(Math.max(min, max === undefined ? next : Math.min(next, max)));
  // Surface-gray ends against a white middle, so the count reads as the value and the glyphs as the
  // controls. `overflow-hidden` lets those ends fill their corners inside the rounded border.
  const buttonClass = "bg-neutral-surface-gray px-[11px] py-[11px] text-primary disabled:text-neutral-silver-gray";

  return (
    <div className={clsx("inline-flex items-stretch overflow-hidden rounded-lg border border-neutral-mid-gray", className)}>
      <button
        type="button"
        className={buttonClass}
        disabled={disabled || value <= min}
        aria-label={`Remove one ${label}`}
        onClick={() => step(value - 1)}
      >
        <Icon name="Minus" size="sm" />
      </button>
      <div className="flex min-w-[48px] items-center justify-center border-x border-neutral-mid-gray bg-white px-2">
        <Typography variant="title" className="tabular-nums text-neutral-dark-gray">
          {value}
        </Typography>
      </div>
      <button
        type="button"
        className={buttonClass}
        disabled={disabled || (max !== undefined && value >= max)}
        aria-label={`Add another ${label}`}
        onClick={() => step(value + 1)}
      >
        <Icon name="Add" size="sm" />
      </button>
    </div>
  );
};
